import {
  ButtonProps,
  PopoverBodyProps,
  PopoverRootProps,
  Spinner,
  useDisclosure,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { forwardRef, useImperativeHandle } from 'react';
import { apiGet } from '../../../api/axios';
import MAlert from '../MAlert';
import MButton from '../MButton';
import MPopoverWrapper from '../MPopoverWrapper';

export function getRecordLookupQueryKey(recordId: string, endpoint: string) {
  return ['record-lookup', recordId, endpoint];
}

export type RecordLookupPopoverRef = { open: () => void; close: () => void };

/*
 * v2 used Popper's `auto-start`, which opened on the side with the most room:
 * usually beside the link. zag has no `auto`, so open to the right and fall
 * back through the other sides when the popover doesn't fit there.
 */
const LOOKUP_POSITIONING: PopoverRootProps['positioning'] = {
  flip: ['left-start', 'bottom-start', 'top-start'],
};

interface RecordLookupPopoverProps {
  recordId: string;
  endpoint: string;
  popoverProps?: Omit<PopoverRootProps, 'children'>;
  buttonProps?: ButtonProps;
  renderRecord: (record: Record<string, unknown>) => React.ReactNode;
  onOpen?: () => void;
  onClose?: () => void;
  showCloseBtn?: boolean;
  popoverBodyProps?: PopoverBodyProps;
  /**
   * By default a cmd/ctrl + click (or middle-click) on the trigger navigates
   * directly to the record (opening in a new tab) instead of opening the
   * popover. The destination is the `/{recordId}` short URL which
   * `IdPrefixNavigator` resolves to the record's full route. Set this to opt
   * out and always open the popover.
   */
  disableCmdClickToOpenInNewTab?: boolean;
  children: React.ReactNode;
}

export const RecordLookupPopover = forwardRef<
  RecordLookupPopoverRef,
  RecordLookupPopoverProps
>(
  (
    {
      recordId,
      endpoint,
      popoverProps,
      renderRecord,
      onOpen,
      onClose,
      showCloseBtn = true,
      popoverBodyProps,
      disableCmdClickToOpenInNewTab = false,
      children,
      buttonProps,
    }: RecordLookupPopoverProps,
    ref,
  ) => {
    const {
      open,
      onClose: internalOnClose,
      onOpen: internalOnOpen,
    } = useDisclosure();

    useImperativeHandle(ref, (): RecordLookupPopoverRef => ({
      open: () => {
        internalOnOpen();
        onOpen?.();
      },
      close: () => {
        internalOnClose();
        onClose?.();
      },
    }));

    const {
      data: record,
      isLoading,
      error,
    } = useQuery({
      queryKey: getRecordLookupQueryKey(recordId, endpoint),
      queryFn: () => apiGet(endpoint).then((res) => res.data),
      enabled: open && !!recordId,
    });

    return (
      <MPopoverWrapper
        showArrow
        usePortal
        placement="right-start"
        isOpen={open}
        onClose={() => {
          internalOnClose();
          if (onClose) {
            onClose();
          }
        }}
        popoverProps={{
          size: 'lg',
          positioning: LOOKUP_POSITIONING,
          ...popoverProps,
        }}
        popoverContentProps={{
          width: '500px',
          // Prevent clicks on the popover from bubbling up to parent elements.
          // e.g. if the popover is inside a clickable table row.
          onClick: (ev) => ev.stopPropagation(),
        }}
        popoverBodyProps={{
          maxH: '85vh',
          overflowY: 'auto',
          width: '500px',
          // Reserve right gutter so consumer content (Edit/Cancel/Save
          // buttons in renderRecord) doesn't crowd the popover close button.
          pr: 9,
          ...popoverBodyProps,
        }}
        triggerContent={
          <MButton
            variant="link"
            size="sm"
            height="auto"
            textAlign="left"
            userSelect="unset"
            wordBreak="break-word"
            whiteSpace="normal"
            lineClamp={1}
            // `lineClamp` makes the trigger a block, which fills a table cell.
            // zag centres the arrow on the trigger, so shrink it to the text
            // or the arrow points at empty space beside the label.
            w="fit-content"
            maxW="full"
            // Render as an anchor pointing at the `/{recordId}` short URL so a
            // cmd/ctrl + click (or middle-click) opens the record in a new tab
            // natively. IdPrefixNavigator resolves the short URL to the full
            // record route.
            {...(!disableCmdClickToOpenInNewTab && {
              as: 'a',
              href: `/${recordId}`,
            })}
            {...buttonProps}
            onClick={(event) => {
              event.stopPropagation();
              // Let the browser handle modified clicks (new tab / new window).
              if (
                !disableCmdClickToOpenInNewTab &&
                (event.metaKey || event.ctrlKey || event.shiftKey)
              ) {
                return;
              }
              event.preventDefault();
              internalOnOpen();
              onOpen?.();
            }}
          >
            {children}
          </MButton>
        }
        showCloseBtn={showCloseBtn}
        bodyContent={
          <>
            {isLoading && <Spinner size="md" />}
            {!isLoading && !record && error && (
              <MAlert
                alertIconProps={{ boxSize: 4 }}
                alertDescriptionProps={{ fontSize: 'sm' }}
                alignSelf="stretch"
                type="error"
              >
                There was an error loading the record.
              </MAlert>
            )}
            {!isLoading && record && renderRecord(record)}
          </>
        }
      />
    );
  },
);
